/**
 * 管理画面 — 行動 approval queue, 確定, history, comments, roster.
 * Server is the boundary: every call is role- and store-checked in goals.py.
 */
import { apiFetch } from './authApi';
import { getApiBaseUrl } from './apiBase';

const API_BASE_URL = getApiBaseUrl();

async function getJson(url) {
  const response = await apiFetch(url);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `${response.status} ${response.statusText}`);
  }
  return response.json();
}

const q = (params) =>
  Object.entries(params)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

/** 確認中 queue. `store` optional (all stores in scope when omitted). */
export function fetchPendingApprovals({ store } = {}) {
  const qs = q({ store });
  return getJson(`${API_BASE_URL}/dashboard/goals/pending-approvals${qs ? `?${qs}` : ''}`);
}

/** 行動記録管理 — approved submissions, newest first. */
export function fetchApprovedSubmissions({ store, limit } = {}) {
  const qs = q({ store, limit });
  return getJson(`${API_BASE_URL}/dashboard/goals/approved-submissions${qs ? `?${qs}` : ''}`);
}

/**
 * 確定. `actions` = the manager's final version ({ actionId?, name, cadence }).
 * 409 = the submission is no longer pending (stale screen) — reload the queue.
 */
export async function approveSubmission({ submissionId, store, stylist, actions, managerNote }) {
  const response = await apiFetch(
    `${API_BASE_URL}/dashboard/goals/submissions/${encodeURIComponent(submissionId)}/approve`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ store, stylist, actions, managerNote: managerNote || null }),
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const e = new Error(err.detail || `${response.status} ${response.statusText}`);
    e.status = response.status;
    throw e;
  }
  return response.json();
}

/**
 * 目標・注力指標 の 確定. `patch` = the manager's corrections only (finalSales / 追加KGI / 注力①②③),
 * empty object = 確定 as submitted. 409 = no longer pending, 400 = KGI / 注力 conflict.
 */
export async function approveGoalSubmission({ submissionId, store, stylist, patch, managerNote }) {
  const response = await apiFetch(
    `${API_BASE_URL}/dashboard/goals/goal-submissions/${encodeURIComponent(submissionId)}/approve`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ store, stylist, patch: patch || {}, managerNote: managerNote || null }),
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const e = new Error(err.detail || `${response.status} ${response.statusText}`);
    e.status = response.status;
    throw e;
  }
  return response.json();
}

/** Stylist コメント tab — managerNote history, newest first. */
export function fetchManagerComments({ store, stylist }) {
  return getJson(`${API_BASE_URL}/dashboard/goals/comments?${q({ store, stylist })}`);
}

/**
 * スタイリスト一覧 — the whole table (BQ KPIs + goals + 行動実施率 + status + role),
 * joined on the server in one call. `month` = YYYY-MM.
 */
export function fetchAdminRoster({ store, month }) {
  return getJson(`${API_BASE_URL}/dashboard/admin/roster?${q({ store, month })}`);
}

// ---------------------------------------------------------------------------
// Prefetch at login — the 管理者 tab opens on data that is already in memory and
// refreshes it in the background (stale-while-revalidate). Kept for the tab session.
// ---------------------------------------------------------------------------

const _cache = new Map(); // key → data

export const adminCacheKey = {
  roster: (store, month) => `roster|${store}|${month}`,
  pending: () => 'pending',
  approved: () => 'approved',
};

export function peekAdminCache(key) {
  return _cache.has(key) ? _cache.get(key) : null;
}

export function putAdminCache(key, data) {
  _cache.set(key, data);
}

async function load(key, fetcher) {
  const data = await fetcher();
  _cache.set(key, data);
  return data;
}

export const loadAdminRoster = (store, month) =>
  load(adminCacheKey.roster(store, month), () => fetchAdminRoster({ store, month }));
export const loadPendingApprovals = () => load(adminCacheKey.pending(), () => fetchPendingApprovals());
export const loadApprovedSubmissions = () =>
  load(adminCacheKey.approved(), () => fetchApprovedSubmissions());

/** Fire-and-forget: called once the signed-in user is known to have 管理画面. */
export function prefetchAdminData({ store, month }) {
  const jobs = [loadPendingApprovals(), loadApprovedSubmissions()];
  if (store && month) jobs.push(loadAdminRoster(store, month));
  return Promise.allSettled(jobs);
}
